import './index.scss';

import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import { autoUpdate, arrow, offset, Placement, useFloating, FloatingArrow, shift } from '@floating-ui/react';

import t from 'utils/i18n';
import { BraxButton } from '../BraxButton';

export type BraxTourOnboardingStep = {
    target: string,
    content: ReactNode,
    placement?: Placement
};

export type BraxTourOnboardingAction = 'start' | 'back' | 'next' | 'end';

export type BraxTourOnboardingProps = {
    steps: BraxTourOnboardingStep[],
    onStateChange?: (action: BraxTourOnboardingAction, step: BraxTourOnboardingStep & { index: number }) => void
};

export const BraxTourOnboarding: FunctionComponent<BraxTourOnboardingProps> = (props) => {
    const [stepIndex, setStepIndex] = useState(0);

    const step = props.steps[stepIndex];

    const [target, setTarget] = useState<HTMLElement | null>(null);

    useEffect(() => {
        document.querySelector('html')?.style.setProperty('overflow', 'hidden');

        // Verifica se o alvo da primeira etapa existe, se não espera um pouco e verifica novamente.
        // Isso porque esse componente pode ser renderizado antes do alvo do tutorial
        const checkTarget = () => {
            const target = document.querySelector<HTMLElement>(props.steps[0].target);

            if (target) {
                setTarget(target);

                trigger('start', 0);
            } else {
                setTimeout(() => checkTarget(), 500);
            }
        }

        checkTarget();
    }, []);

    const arrowRef = useRef<SVGSVGElement>(null);

    const { refs, floatingStyles, context } = useFloating({
        nodeId: `${stepIndex}`,
        elements: { reference: target },
        placement: step.placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(16),
            shift(),
            arrow({ element: arrowRef })
        ]
    });

    const trigger = (action: BraxTourOnboardingAction, index: number) => {
        if (action === 'end') {
            setTarget(null);

            document.querySelector('html')?.style.removeProperty('overflow');
        } else {
            setStepIndex(index);

            setTarget(document.querySelector<HTMLElement>(props.steps[index].target));
        }

        props.onStateChange?.(action, { index, ...props.steps[index] });
    };

    if (target === null)
        return null;

    const { top, left, width, height } = target.getBoundingClientRect();

    return (<>
        <div className="brax-tour-onboarding" ref={refs.setFloating} style={floatingStyles}>
            <FloatingArrow ref={arrowRef} context={context} width={32} height={16} />

            <div className={`brax-tour-onboarding-tooltip brax-tooltip-${stepIndex}`}>
                <div>{ step.content }</div>

                <div>
                    { stepIndex > 0 ? (
                        <BraxButton
                            variant="secondary"
                            size="md"
                            onClick={() => trigger('back', stepIndex - 1)}
                        >
                            { t('Voltar') }
                        </BraxButton>
                    ) : null }

                    <BraxButton
                        variant="primary"
                        size="md"
                        onClick={() => stepIndex === props.steps.length - 1 ? trigger('end', stepIndex) : trigger('next', stepIndex + 1)}
                    >
                        { t(stepIndex === props.steps.length - 1 ? 'Terminar' : 'Próximo') }
                        { ' ' }
                        ({ stepIndex + 1 }/{ props.steps.length })
                    </BraxButton>
                </div>
            </div>
        </div>

        <div className="brax-overlay">
            <div className="brax-spotlight" style={{ top, left, width, height }} />
        </div>
    </>);
}
